import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import ReactDOM from 'react-dom';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TextInput,
  TouchableOpacity,
  ActivityIndicator,
  Platform,
} from 'react-native';
import { useNavigation, useRoute, RouteProp } from '@react-navigation/native';
import { NativeStackNavigationProp } from '@react-navigation/native-stack';
import Svg, { Path } from 'react-native-svg';
import { COLORS, FONTS } from '../../constants/config';
import { PageContainer, Breadcrumb, KPIDeleteConfirmModal, MultiSelectDropdown } from '../../components';
import CancelConfirmModal from '../../components/CancelConfirmModal';
import { useCancelConfirm } from '../../hooks/useCancelConfirm';
import { MainStackParamList } from '../../navigation/MainNavigator';
import { unitGoalsApi, goalsApi, organizationalUnitsApi, kpisApi } from '../../services/apiService';
import { useNotification } from '../../contexts/NotificationContext';
import { useTheme } from '../../contexts/ThemeContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { getTextAlign } from '../../utils/rtl';
import { useRoleNavigation, useUnsavedChangesWarning } from '../../hooks';
import { queryClient, queryKeys } from '../../providers/QueryProvider';
import { extractKpiErrorMessage, validateKpiRequiredFields } from '../../utils/kpiErrors';
import { normalizeArabicNumbers } from '../../utils/rtl';
import { useAutoTranslate } from '../../hooks/useAutoTranslate';
import { StrategicGoalList, OrganizationalUnitList, KPIType, Frequency, Polarity, KPIList } from '../../types';

import { tokens } from '@design';
import { Button } from '@design/components/Button';
import KpiAnnualTargetSection, {
  KpiTargetConfig,
  emptyKpiTargetConfig,
  configFromKpi,
  buildKpiTargetPayload,
} from '../kpis/components/KpiAnnualTargetSection';
type RouteProps = RouteProp<MainStackParamList, 'AddUnitGoal'>;
type NavigationProp = NativeStackNavigationProp<MainStackParamList>;

// ============================================================
// KPI Form Interface
// ============================================================

interface KPIFormData {
  id: number;
  name: string;
  weight: string;
  description: string;
  kpiType: KPIType | '';
  polarity: Polarity | '';
  startDate: string;
  unit: string;
  frequency: Frequency | '';
  formula: string;
  targetValue: string;
  worstAcceptableValue: string;
  dataSource: string;
  target: KpiTargetConfig;
  isExpanded: boolean;
  isExisting?: boolean;  // Flag to identify existing KPIs from API
  apiId?: number;        // Original API ID for existing KPIs
  responsibleUnits: string[]; // Units responsible for executing this KPI (restricted to the unit goal's own unit)
}

// ============================================================
// SVG Icon Components
// ============================================================

const ChevronLeftIcon = ({ size = 16, color = tokens.colors.raw.gray["500"] }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M15 18L9 12L15 6" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

const ChevronDownIcon = ({ size = 16, color = tokens.colors.raw.gray["500"] }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M6 9L12 15L18 9" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

const ChevronUpIcon = ({ size = 16, color = tokens.colors.raw.gray["500"] }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M18 15L12 9L6 15" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

const TrashIcon = ({ size = 16, color = tokens.colors.raw.gray["500"] }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M3 6H5H21" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M8 6V4C8 3.46957 8.21071 2.96086 8.58579 2.58579C8.96086 2.21071 9.46957 2 10 2H14C14.5304 2 15.0391 2.21071 15.4142 2.58579C15.7893 2.96086 16 3.46957 16 4V6M19 6V20C19 20.5304 18.7893 21.0391 18.4142 21.4142C18.0391 21.7893 17.5304 22 17 22H7C6.46957 22 5.96086 21.7893 5.58579 21.4142C5.21071 21.0391 5 20.5304 5 20V6H19Z" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

const PlusIcon = ({ size = 16, color = tokens.colors.semantic.general.foreground }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M12 5V19M5 12H19" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

const DragHandleIcon = ({ size = 20, color = tokens.colors.raw.gray["400"] }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M8 6H8.01M8 12H8.01M8 18H8.01M16 6H16.01M16 12H16.01M16 18H16.01" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

const CalendarIcon = ({ size = 16, color = tokens.colors.raw.gray["500"] }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M8 2V6M16 2V6M3 10H21M5 4H19C20.1046 4 21 4.89543 21 6V20C21 21.1046 20.1046 22 19 22H5C3.89543 22 3 21.1046 3 20V6C3 4.89543 3.89543 4 5 4Z" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

// ============================================================
// Dropdown Component
// ============================================================

interface DropdownOption {
  value: string;
  label: string;
}

interface DropdownProps {
  value: string;
  options: DropdownOption[];
  placeholder: string;
  onChange: (value: string) => void;
  disabled?: boolean;
}

const Dropdown = ({ value, options, placeholder, onChange, disabled }: DropdownProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [position, setPosition] = useState({ top: 0, left: 0, width: 0 });
  const { colors, isDark } = useTheme();
  const buttonRef = useRef<View>(null);
  const selectedOption = options.find(opt => opt.value === value);

  const dropdownTheme = {
    bg: colors.card,
    border: colors.border,
    text: colors.textPrimary,
    placeholder: isDark ? tokens.colors.raw.gray["500"] : tokens.colors.raw.gray["400"],
    icon: colors.textMuted,
    disabledBg: isDark ? colors.backgroundAlt : tokens.colors.raw.slate["100"],
    menuBg: colors.card,
    menuBorder: colors.border,
    itemActiveBg: isDark ? '#451A1A' : tokens.colors.raw.red["50"],
  };

  const handleOpen = () => {
    if (disabled) return;

    if (!isOpen && buttonRef.current) {
      // Use measureInWindow to get button position
      buttonRef.current.measureInWindow((x, y, width, height) => {
        setPosition({
          top: y + height + 4, // 4px gap below button
          left: x,
          width: width,
        });
      });
    }
    setIsOpen(!isOpen);
  };

  // Close on outside click and scroll
  const menuDomRef = useRef<HTMLDivElement | null>(null);
  const menuCallbackRef = useCallback((node: any) => {
    menuDomRef.current = node as HTMLDivElement | null;
  }, []);

  useEffect(() => {
    if (!isOpen) return;

    const handleMouseDown = (e: MouseEvent) => {
      const menuEl = menuDomRef.current;
      if (menuEl && menuEl.contains(e.target as Node)) return;
      setIsOpen(false);
    };

    const handleScroll = (e: Event) => {
      const menuEl = menuDomRef.current;
      if (menuEl && menuEl.contains(e.target as Node)) return;
      setIsOpen(false);
    };

    document.addEventListener('mousedown', handleMouseDown);
    window.addEventListener('scroll', handleScroll, true); // capture phase

    return () => {
      document.removeEventListener('mousedown', handleMouseDown);
      window.removeEventListener('scroll', handleScroll, true);
    };
  }, [isOpen]);

  const menu = isOpen ? (
    <View
      ref={menuCallbackRef}
      // @ts-ignore - web style
      onMouseDown={(e: any) => e.stopPropagation()}
      style={{
        position: 'fixed' as const,
        top: position.top,
        start: position.left,
        width: position.width,
        backgroundColor: dropdownTheme.menuBg,
        borderWidth: tokens.spacing.absolute["px"],
        borderColor: dropdownTheme.menuBorder,
        borderRadius: tokens.radii.absolute["radius-8"],
        maxHeight: 200,
        boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.15)',
        zIndex: 99999,
        overflow: 'hidden' as const,
      }}
    >
      <ScrollView style={styles.dropdownScroll} nestedScrollEnabled>
        {options.map((option) => (
          <TouchableOpacity
            key={option.value}
            style={[
              styles.dropdownItem,
              { backgroundColor: dropdownTheme.bg },
              value === option.value && { backgroundColor: dropdownTheme.itemActiveBg }
            ]}
            onPress={() => {
              onChange(option.value);
              setIsOpen(false);
            }}
          >
            <Text style={[
              styles.dropdownItemText,
              { color: dropdownTheme.text },
              value === option.value && styles.dropdownItemTextActive
            ]}>
              {option.label}
            </Text>
          </TouchableOpacity>
        ))}
      </ScrollView>
    </View>
  ) : null;

  return (
    <View style={[styles.dropdownContainer, isOpen && styles.dropdownContainerOpen]}>
      <TouchableOpacity
        ref={buttonRef}
        style={[
          styles.dropdownButton,
          { backgroundColor: dropdownTheme.bg, borderColor: dropdownTheme.border },
          disabled && { backgroundColor: dropdownTheme.disabledBg },
          isOpen && styles.dropdownButtonOpen
        ]}
        onPress={handleOpen}
        disabled={disabled}
      >
        <Text style={[
          styles.dropdownButtonText,
          { color: dropdownTheme.text },
          !selectedOption && { color: dropdownTheme.placeholder }
        ]}>
          {selectedOption?.label || placeholder}
        </Text>
        <ChevronDownIcon size={16} color={dropdownTheme.icon} />
      </TouchableOpacity>
      {menu && ReactDOM.createPortal(menu, document.body)}
    </View>
  );
};

// ============================================================
// Main Component
// ============================================================

export default function AddUnitGoalScreen() {
  const navigation = useNavigation<NavigationProp>();
  const route = useRoute<RouteProps>();
  const { unitGoalId, strategicGoalId, unitId, scrollToKpi } = route.params || {};
  const { showSuccess, showError } = useNotification();
  const { navigateToHome, isViewOnly } = useRoleNavigation();
  const { colors, isDark } = useTheme();
  const { t, language } = useLanguage();
  const isRTL = language === 'ar';
  const styles = useMemo(() => getStyles(isRTL), [isRTL]);
  const { translate, translateUnit } = useAutoTranslate();

  // KPI Options - defined inside component to use translations
  const kpiTypeOptions: DropdownOption[] = [
    { value: 'strategic', label: t('kpis.strategic') },
    { value: 'operational', label: t('kpis.operational') },
    { value: 'efficiency', label: t('kpis.efficiency') },
    { value: 'effectiveness', label: t('kpis.effectiveness') },
    { value: 'impact', label: t('kpis.impact') },
  ];

  const polarityOptions: DropdownOption[] = [
    { value: 'positive', label: t('kpis.ascending') },
    { value: 'negative', label: t('kpis.descending') },
  ];

  const unitOptions: DropdownOption[] = [
    { value: '%', label: t('kpis.percentage') },
    { value: 'number', label: t('kpis.number') },
    { value: 'currency', label: t('kpis.currency') },
  ];

  const frequencyOptions: DropdownOption[] = [
    { value: 'daily', label: t('kpis.daily') },
    { value: 'weekly', label: t('kpis.weekly') },
    { value: 'monthly', label: t('kpis.monthly') },
    { value: 'quarterly', label: t('kpis.quarterly') },
    { value: 'half_yearly', label: t('kpis.semiAnnual') },
    { value: 'yearly', label: t('kpis.annual') },
  ];

  // Theme colors for form elements
  const themeColors = {
    mainCard: colors.card,
    mainCardBorder: colors.cardBorder,
    pageTitle: colors.textPrimary,
    section: isDark ? colors.backgroundAlt : tokens.colors.raw.slate["50"],
    sectionTitle: colors.textPrimary,
    label: colors.textPrimary,
    input: colors.card,
    inputBorder: colors.border,
    inputText: colors.textPrimary,
    placeholder: isDark ? tokens.colors.raw.gray["500"] : tokens.colors.raw.gray["400"],
    iconColor: colors.textMuted,
    kpiCard: colors.card,
    kpiCardBorder: colors.cardBorder,
    kpiCardHeader: colors.card,
    kpiNumber: isDark ? colors.backgroundAlt : tokens.colors.raw.slate["100"],
  };

  // Redirect view-only users (executive) - they cannot add/edit
  useEffect(() => {
    if (isViewOnly) {
      console.log('🚫 AddUnitGoal: View-only user - redirecting');
      navigation.goBack();
    }
  }, [isViewOnly, navigation]);

  // Refs for scrolling to KPI section
  const scrollViewRef = useRef<ScrollView>(null);
  const kpiSectionY = useRef<number>(0);

  // Edit mode flag
  const isEditMode = !!unitGoalId;

  // Form state - pre-select unit if unitId is provided
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    strategicGoal: strategicGoalId ? strategicGoalId.toString() : '',
    unit: unitId ? unitId.toString() : '',
    weight: '30',
  });

  // KPIs state - initialize empty, only load from API in edit mode
  const [kpis, setKpis] = useState<KPIFormData[]>([]);
  const [deletedKpiIds, setDeletedKpiIds] = useState<number[]>([]);
  const [kpiPendingDelete, setKpiPendingDelete] = useState<{ localId: number; backendId: number | null; name: string } | null>(null);
  const [nextKpiId, setNextKpiId] = useState(1);
  const [existingKpisLoading, setExistingKpisLoading] = useState(false);


  const [loading, setLoading] = useState(false);
  const [goalsLoading, setGoalsLoading] = useState(true);
  const [unitsLoading, setUnitsLoading] = useState(true);
  const [initialLoading, setInitialLoading] = useState(isEditMode);

  const { requestCancel, confirmVisible, confirmDiscard, dismissConfirm } = useCancelConfirm({
    snapshot: { formData, kpis },
    ready: !initialLoading,
    onExit: () => navigation.goBack(),
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [goals, setGoals] = useState<StrategicGoalList[]>([]);
  const [units, setUnits] = useState<OrganizationalUnitList[]>([]);
  const [kpiSectionExpanded, setKpiSectionExpanded] = useState(true);

  useUnsavedChangesWarning(!!formData.name || !!formData.description);

  // Fetch goals and units for dropdowns
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [goalsResponse, unitsResponse] = await Promise.all([
          goalsApi.list({ page_size: 100 }),
          organizationalUnitsApi.list({ page_size: 9999 }),
        ]);
        setGoals(goalsResponse.results || []);
        setUnits(unitsResponse.results || []);
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setGoalsLoading(false);
        setUnitsLoading(false);
      }
    };
    fetchData();
  }, []);

  // Fetch unit goal data when in edit mode
  useEffect(() => {
    const fetchUnitGoal = async () => {
      if (!unitGoalId) return;

      try {
        setInitialLoading(true);
        const unitGoal = await unitGoalsApi.get(unitGoalId);

        // Populate form with unit goal data
        setFormData({
          name: unitGoal.name || '',
          description: unitGoal.description || '',
          strategicGoal: unitGoal.strategic_goal?.toString() || '',
          unit: unitGoal.unit?.toString() || '',
          weight: unitGoal.weight?.toString() || '30',
        });

        // Fetch KPIs linked directly to this unit goal and convert to editable form data
        setExistingKpisLoading(true);
        try {
          const kpisData = await kpisApi.list({ organizational_unit_goal: unitGoalId, page_size: 100 });

          // Convert API KPIs to KPIFormData format for editing
          const existingKpiData: KPIFormData[] = (kpisData.results || []).map((kpi: KPIList, index: number) => ({
            id: -(index + 1), // Use negative IDs for existing KPIs to avoid conflicts
            name: kpi.name,
            weight: (kpi as any).weight?.toString() || '30',
            description: kpi.description || '',
            kpiType: (kpi.kpi_type || 'operational') as KPIType,
            polarity: (kpi as any).polarity || 'positive',
            startDate: (kpi as any).measurement_start_date || '',
            unit: kpi.unit || '',
            frequency: (kpi.frequency || 'monthly') as Frequency,
            formula: (kpi as any).measurement_formula || '',
            targetValue: kpi.target_value || '0',
            worstAcceptableValue: (kpi as any).worst_acceptable_value != null ? String((kpi as any).worst_acceptable_value) : '',
            dataSource: (kpi as any).data_source || '',
            target: configFromKpi(kpi),
            isExpanded: false,
            isExisting: true,
            apiId: kpi.id,
            responsibleUnits: ((kpi as any).responsible_units || []).map((id: number) => String(id)),
          }));

          setKpis(existingKpiData);
          setNextKpiId(existingKpiData.length + 1);
        } catch (err) {
          console.error('Error fetching KPIs:', err);
        } finally {
          setExistingKpisLoading(false);
        }
      } catch (error) {
        console.error('Error fetching unit goal:', error);
        showError(t('employees.loadFailed'));
      } finally {
        setInitialLoading(false);
      }
    };

    fetchUnitGoal();
  }, [unitGoalId]);

  // Scroll to KPI section function
  const scrollToKpiSection = useCallback(() => {
    if (scrollToKpi && kpiSectionY.current > 0) {
      setTimeout(() => {
        scrollViewRef.current?.scrollTo({ y: kpiSectionY.current - 100, animated: true });
      }, 100);
    }
  }, [scrollToKpi]);

  // Scroll to KPI section when data is loaded
  useEffect(() => {
    if (!initialLoading && scrollToKpi) {
      scrollToKpiSection();
    }
  }, [initialLoading, scrollToKpi, scrollToKpiSection]);

  const goalOptions: DropdownOption[] = goals.map(goal => ({
    value: goal.id.toString(),
    label: translate(goal.title),
  }));

  const unitOptionsDropdown: DropdownOption[] = units.map(unit => ({
    value: unit.id.toString(),
    label: translateUnit(unit.name_ar || unit.name),
  }));

  // Update form field
  const updateField = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  // Add new KPI
  const handleAddKPI = () => {
    const newKpi: KPIFormData = {
      id: nextKpiId,
      name: '',
      weight: '30',
      description: '',
      kpiType: 'operational',
      polarity: '',
      startDate: '',
      unit: '%',
      frequency: 'yearly',
      formula: '',
      targetValue: '0.0',
      worstAcceptableValue: '',
      dataSource: '',
      target: emptyKpiTargetConfig,
      isExpanded: true,
      isExisting: false, // new KPI: validator must read the grid, not the scalar targetValue
      responsibleUnits: formData.unit ? [formData.unit] : [], // pre-select the unit goal's own unit
    };
    setKpis([...kpis, newKpi]);
    setNextKpiId(nextKpiId + 1);
  };

  // Update KPI field
  const updateKPI = (id: number, data: Partial<KPIFormData>) => {
    setKpis(kpis.map(kpi => kpi.id === id ? { ...kpi, ...data } : kpi));
  };

  // Remove KPI — open confirmation first
  const deleteKPI = (id: number) => {
    const kpiToRemove = kpis.find(kpi => kpi.id === id);
    const backendId = kpiToRemove?.isExisting && kpiToRemove.apiId ? kpiToRemove.apiId : null;
    setKpiPendingDelete({
      localId: id,
      backendId,
      name: (kpiToRemove as any)?.name || '',
    });
  };

  const confirmDeleteKPI = () => {
    if (!kpiPendingDelete) return;
    const { localId, backendId } = kpiPendingDelete;
    if (backendId !== null) {
      setDeletedKpiIds(prev => [...prev, backendId]);
    }
    setKpis(prev => prev.filter(kpi => kpi.id !== localId));
    setKpiPendingDelete(null);
  };

  // Toggle KPI expand/collapse
  const toggleKPIExpand = (id: number) => {
    setKpis(kpis.map(kpi => kpi.id === id ? { ...kpi, isExpanded: !kpi.isExpanded } : kpi));
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    const missing: string[] = [];
    if (!formData.name.trim()) {
      newErrors.name = t('unitGoals.goalNameRequired');
      missing.push(t('unitGoals.goalNameRequired'));
    }
    if (!formData.unit) {
      newErrors.unit = t('unitGoals.unitRequired');
      missing.push(t('unitGoals.unitRequired'));
    }

    setErrors(newErrors);
    // Surface the SPECIFIC missing fields (not a generic "حقل مطلوب").
    if (missing.length > 0) {
      showError(missing.join('\n'));
      return false;
    }

    const kpiError = validateKpiRequiredFields(kpis, t);
    if (kpiError) { showError(kpiError); return false; }
    return true;
  };

  const handleSave = async () => {
    // validateForm already shows a specific error for whatever is missing.
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    try {
      const unitGoalData = {
        name: formData.name,
        description: formData.description,
        strategic_goal: formData.strategicGoal ? parseInt(formData.strategicGoal) : null,
        unit: parseInt(formData.unit),
        weight: formData.weight,
      };

      let savedUnitGoalId: number;

      if (isEditMode && unitGoalId) {
        await unitGoalsApi.update(unitGoalId, unitGoalData);
        savedUnitGoalId = unitGoalId;
      } else {
        const response = await unitGoalsApi.create(unitGoalData);
        savedUnitGoalId = response.id;
      }

      // Delete removed KPIs from backend
      let kpiDeleteFailed = 0;
      if (deletedKpiIds.length > 0) {
        for (const kpiId of deletedKpiIds) {
          try {
            await kpisApi.delete(kpiId);
          } catch (deleteError: any) {
            kpiDeleteFailed++;
          }
        }
      }
      if (kpiDeleteFailed > 0) {
        showError(t('kpis.deleteFailed'));
      }

      // Handle KPIs - update existing ones and create new ones
      const kpiErrors: string[] = [];
      for (const kpi of kpis) {
        if (!kpi.name) continue;
        const kpiData = {
          name: kpi.name,
          description: kpi.description || '',
          kpi_type: kpi.kpiType || 'operational',
          unit: kpi.unit || '%',
          target_value: (parseFloat(kpi.targetValue) || 0).toFixed(2),
          worst_acceptable_value:
            kpi.polarity === 'negative' && kpi.unit !== '%' && kpi.worstAcceptableValue !== ''
              ? (parseFloat(kpi.worstAcceptableValue) || 0).toFixed(2)
              : null,
          frequency: kpi.frequency || 'monthly',
          polarity: kpi.polarity || 'positive',
          weight: kpi.weight || '30',
          weight_from_source: '30',
          achieved_value: '0',
          source_type: 'unit_goal',
          evidence_required: false,
          measurement_start_date: kpi.startDate || null,
          measurement_formula: kpi.formula || '',
          data_source: kpi.dataSource || '',
          owner: kpi.dataSource || t('common.notSpecified'),
          organizational_unit_goal: savedUnitGoalId,
          responsible_units: kpi.responsibleUnits.map(Number),
        };

        if (kpi.isExisting && kpi.apiId) {
          try {
            await kpisApi.update(kpi.apiId, {
              ...kpiData,
              ...buildKpiTargetPayload(kpi.target, {
                unit: kpi.unit,
                frequency: kpi.frequency,
                startDate: kpi.startDate,
              }),
            });
          } catch (updateError: any) {
            const errMsg = extractKpiErrorMessage(updateError?.response?.data);
            kpiErrors.push(t('kpis.kpiUpdateError', { name: kpi.name, error: errMsg }));
            console.warn(`KPI ${kpi.apiId} update failed:`, updateError?.response?.data);
          }
        } else {
          try {
            await kpisApi.create({
              ...kpiData,
              ...buildKpiTargetPayload(kpi.target, {
                unit: kpi.unit,
                frequency: kpi.frequency,
                startDate: kpi.startDate,
              }),
            });
          } catch (createError: any) {
            const errMsg = extractKpiErrorMessage(createError?.response?.data);
            kpiErrors.push(t('kpis.kpiSaveError', { name: kpi.name, error: errMsg }));
            console.warn(`KPI create failed:`, createError?.response?.data);
          }
        }
      }

      if (kpiErrors.length > 0) {
        showError(kpiErrors.join('\n'));
      }

      showSuccess(isEditMode ? t('unitGoals.updateSuccess') : t('unitGoals.createSuccess'));
      queryClient.invalidateQueries({ queryKey: queryKeys.unitGoals.all });
      // Go to the saved unit-goal's detail page (e.g. /unit-goals/28) instead of
      // goBack(), which can land on the dashboard when the stack is shallow.
      navigation.navigate('UnitGoalDetail', { id: savedUnitGoalId });
    } catch (error: any) {
      console.error('Error saving unit goal:', error);
      console.error('Error response data:', error.response?.data);
      let errorMsg = t('unitGoals.saveFailed');
      const data = error.response?.data;
      if (data) {
        if (typeof data === 'string') {
          errorMsg = data;
        } else if (data.detail) {
          errorMsg = data.detail;
        } else if (typeof data === 'object') {
          // Extract field-level validation errors (e.g. {"name": ["error msg"]})
          const messages = Object.values(data)
            .flat()
            .filter((v): v is string => typeof v === 'string');
          if (messages.length > 0) {
            errorMsg = messages.join('\n');
          }
        }
      }
      showError(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  // Render KPI Card
  const renderKPICard = (kpi: KPIFormData, index: number) => (
    <View key={kpi.id} style={[styles.kpiCard, { backgroundColor: themeColors.kpiCard, borderColor: themeColors.kpiCardBorder }]}>
      {/* KPI Header */}
      <View style={[styles.kpiCardHeader, { backgroundColor: themeColors.kpiCardHeader, borderBottomColor: themeColors.kpiCardBorder }]}>
        <View style={styles.kpiCardHeaderLeft}>
          <TouchableOpacity
            style={styles.kpiHeaderButton}
            onPress={() => toggleKPIExpand(kpi.id)}
          >
            {kpi.isExpanded ? (
              <ChevronUpIcon size={20} color={themeColors.iconColor} />
            ) : (
              <ChevronDownIcon size={20} color={themeColors.iconColor} />
            )}
          </TouchableOpacity>
          <TouchableOpacity
            style={styles.kpiHeaderButton}
            onPress={() => deleteKPI(kpi.id)}
          >
            <TrashIcon size={18} color={themeColors.iconColor} />
          </TouchableOpacity>
        </View>
        <View style={styles.kpiCardHeaderRight}>
          <Text style={[styles.kpiCardName, { color: themeColors.inputText }]}>
            {kpi.name || `${t('kpis.kpiName')} ${index + 1}`}
          </Text>
          <View style={[styles.kpiCardNumber, { backgroundColor: themeColors.kpiNumber }]}>
            <Text style={[styles.kpiCardNumberText, { color: themeColors.inputText }]}>{index + 1}</Text>
          </View>
          <DragHandleIcon size={20} color={themeColors.iconColor} />
        </View>
      </View>

      {/* KPI Content - Collapsible */}
      {kpi.isExpanded && (
        <View style={styles.kpiCardContent}>
          {/* Basic Information Subsection */}
          <View style={[styles.subsection, { backgroundColor: themeColors.section }]}>
            <Text style={[styles.subsectionTitle, { color: themeColors.sectionTitle }]}>{t('kpis.basicInformation')}</Text>

            <View style={styles.row}>
              <View style={styles.fieldSmall}>
                <View style={styles.labelRow}>
                  <Text style={styles.requiredStar}>*</Text>
                  <Text style={[styles.label, { color: themeColors.label }]}>{t('common.weight')}</Text>
                </View>
                <View style={styles.weightInputRow}>
                  <TouchableOpacity style={[styles.setWeightButton, { backgroundColor: themeColors.input, borderColor: themeColors.inputBorder }]}>
                    <Text style={[styles.setWeightButtonText, { color: themeColors.inputText }]}>{t('kpis.setWeight')}</Text>
                  </TouchableOpacity>
                  <Text style={[styles.percentSymbol, { color: themeColors.iconColor }]}>%</Text>
                  <TextInput
                    style={[styles.weightInput, { backgroundColor: themeColors.input, borderColor: themeColors.inputBorder, color: themeColors.inputText }]}
                    value={kpi.weight}
                    onChangeText={(v) => updateKPI(kpi.id, { weight: normalizeArabicNumbers(v) })}
                    keyboardType="numeric"
                    textAlign="right"
                  />
                </View>
              </View>

              <View style={styles.fieldLarge}>
                <View style={styles.labelRow}>
                  <Text style={styles.requiredStar}>*</Text>
                  <Text style={[styles.label, { color: themeColors.label }]}>{t('kpis.kpiName')}</Text>
                </View>
                <TextInput
                  style={[styles.input, { backgroundColor: themeColors.input, borderColor: themeColors.inputBorder, color: themeColors.inputText }]}
                  value={kpi.name}
                  onChangeText={(v) => updateKPI(kpi.id, { name: v })}
                  placeholder=""
                  textAlign="right"
                />
              </View>
            </View>

            <View style={styles.field}>
              <View style={styles.labelRow}>
                <Text style={styles.requiredStar}>*</Text>
                <Text style={[styles.label, { color: themeColors.label }]}>{t('kpis.description')}</Text>
              </View>
              <TextInput
                style={[styles.input, styles.textArea, { backgroundColor: themeColors.input, borderColor: themeColors.inputBorder, color: themeColors.inputText }]}
                value={kpi.description}
                onChangeText={(v) => updateKPI(kpi.id, { description: v })}
                placeholder=""
                textAlign="right"
                multiline
                numberOfLines={3}
              />
            </View>
          </View>

          {/* Classification Subsection */}
          <View style={[styles.subsection, { zIndex: 30, backgroundColor: themeColors.section }]}>
            <Text style={[styles.subsectionTitle, { color: themeColors.sectionTitle }]}>{t('kpis.classification')}</Text>

            <View style={[styles.row, { zIndex: 10 }]}>
              <View style={styles.fieldHalf}>
                <View style={styles.labelRow}>
                  <Text style={styles.requiredStar}>*</Text>
                  <Text style={[styles.label, { color: themeColors.label }]}>{t('kpis.polarity')}</Text>
                </View>
                <Dropdown
                  value={kpi.polarity}
                  options={polarityOptions}
                  placeholder={t('kpis.selectPolarityHint')}
                  onChange={(v) => updateKPI(kpi.id, { polarity: v as Polarity })}
                />
              </View>

              <View style={styles.fieldHalf}>
                <View style={styles.labelRow}>
                  <Text style={styles.requiredStar}>*</Text>
                  <Text style={[styles.label, { color: themeColors.label }]}>{t('kpis.kpiClassification')}</Text>
                </View>
                <Dropdown
                  value={kpi.kpiType}
                  options={kpiTypeOptions}
                  placeholder={t('kpis.selectClassification')}
                  onChange={(v) => updateKPI(kpi.id, { kpiType: v as KPIType })}
                />
              </View>
            </View>

            <View style={{ marginTop: tokens.spacing.absolute["3"], zIndex: 5 }}>
              <Text style={[styles.label, { color: themeColors.label, marginBottom: 8 }]}>{t('initiatives.implementationResponsibility')}</Text>
              <MultiSelectDropdown
                values={kpi.responsibleUnits}
                options={(() => {
                  const opts = units
                    .filter(u => String(u.id) === String(formData.unit))
                    .map(u => ({ value: u.id.toString(), label: translateUnit(u.name_ar || u.name) }));
                  kpi.responsibleUnits.forEach(v => {
                    if (!opts.find(o => o.value === v)) {
                      const found = units.find(u => String(u.id) === v);
                      opts.push(found ? { value: v, label: translateUnit(found.name_ar || found.name) } : { value: v, label: v });
                    }
                  });
                  return opts;
                })()}
                placeholder={t('initiatives.selectResponsibleUnits')}
                onChange={(vals) => updateKPI(kpi.id, { responsibleUnits: vals })}
              />
            </View>
          </View>

          {/* Measurement + annual target grid — shared section for new AND existing KPIs */}
          <KpiAnnualTargetSection
            config={kpi.target}
            onChange={(patch) => updateKPI(kpi.id, { target: { ...kpi.target, ...patch } })}
            unit={kpi.unit}
            frequency={kpi.frequency}
            startDate={kpi.startDate}
            formula={kpi.formula}
            dataSource={kpi.dataSource}
            onMeasurementChange={(field, value) => updateKPI(kpi.id, { [field]: value })}
          />
        </View>
      )}
    </View>
  );

  // Show loading while fetching data in edit mode
  if (initialLoading) {
    return (
      <PageContainer>
        <View style={styles.loadingFullContainer}>
          <ActivityIndicator size="large" color={tokens.colors.semantic.general["Main Color"]} />
          <Text style={[styles.loadingText, { color: isDark ? tokens.colors.raw.gray["400"] : tokens.colors.raw.gray["500"] }]}>{t('common.loadingData')}</Text>
        </View>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <ScrollView ref={scrollViewRef} style={styles.scrollView} contentContainerStyle={styles.content}>
        {/* Breadcrumb */}
        <Breadcrumb
          items={[
            { label: t('navigation.home'), route: 'Dashboard' },
            { label: t('unitGoals.title'), route: 'UnitGoals' },
            { label: isEditMode ? t('unitGoals.editGoal') : t('unitGoals.addNewGoal') },
          ]}
        />

        {/* Page Title */}
        <Text style={[styles.pageTitle, { color: themeColors.pageTitle }]}>{isEditMode ? t('unitGoals.editGoal') : t('unitGoals.addNewGoal')}</Text>

        {/* Main Card Container */}
        <View style={[styles.mainCard, { backgroundColor: themeColors.mainCard, borderColor: themeColors.mainCardBorder, overflow: 'visible', zIndex: 10 }]}>
          {/* Basic Information Section */}
          <View style={[styles.section, { zIndex: 30, overflow: 'visible', backgroundColor: themeColors.section, borderColor: themeColors.inputBorder }]}>
            <Text style={[styles.sectionTitle, { color: themeColors.sectionTitle }]}>{t('kpis.basicInformation')}</Text>

            {/* Row 1: Name + Weight */}
            <View style={styles.row}>
              <View style={styles.fieldHalf}>
                <View style={styles.labelRow}>
                  <Text style={styles.requiredStar}>*</Text>
                  <Text style={[styles.label, { color: themeColors.label }]}>{t('common.weight')}</Text>
                </View>
                <View style={styles.weightInputRow}>
                  <TouchableOpacity style={[styles.setWeightButton, { backgroundColor: themeColors.input, borderColor: themeColors.inputBorder }]}>
                    <Text style={[styles.setWeightButtonText, { color: themeColors.inputText }]}>{t('kpis.setWeight')}</Text>
                  </TouchableOpacity>
                  <Text style={[styles.percentSymbol, { color: themeColors.iconColor }]}>%</Text>
                  <TextInput
                    style={[styles.weightInput, { backgroundColor: themeColors.input, borderColor: themeColors.inputBorder, color: themeColors.inputText }]}
                    value={formData.weight}
                    onChangeText={(v) => updateField('weight', normalizeArabicNumbers(v))}
                    keyboardType="numeric"
                    textAlign="right"
                  />
                </View>
              </View>

              <View style={styles.fieldHalf}>
                <View style={styles.labelRow}>
                  <Text style={styles.requiredStar}>*</Text>
                  <Text style={[styles.label, { color: themeColors.label }]}>{t('common.name')}</Text>
                </View>
                <TextInput
                  style={[styles.input, { backgroundColor: themeColors.input, borderColor: themeColors.inputBorder, color: themeColors.inputText }, errors.name ? styles.inputError : undefined]}
                  value={formData.name}
                  onChangeText={(v) => updateField('name', v)}
                  placeholder={t('unitGoals.enterGoalName')}
                  placeholderTextColor={themeColors.placeholder}
                  textAlign="right"
                />
                {errors.name && <Text style={styles.errorText}>{errors.name}</Text>}
              </View>
            </View>

            {/* Row 2: Description - Full width */}
            <View style={styles.field}>
              <View style={styles.labelRow}>
                <Text style={[styles.label, { color: themeColors.label }]}>{t('common.description')}</Text>
              </View>
              <TextInput
                style={[styles.input, styles.textArea, { backgroundColor: themeColors.input, borderColor: themeColors.inputBorder, color: themeColors.inputText }]}
                value={formData.description}
                onChangeText={(v) => updateField('description', v)}
                placeholder=""
                placeholderTextColor={themeColors.placeholder}
                textAlign="right"
                multiline
                numberOfLines={3}
              />
            </View>

            {/* Row 3: Strategic Goal + Unit */}
            <View style={[styles.row, { zIndex: 20, overflow: 'visible' }]}>
              <View style={[styles.fieldHalf, { zIndex: 10, overflow: 'visible' }]}>
                <View style={styles.labelRow}>
                  <Text style={styles.requiredStar}>*</Text>
                  <Text style={[styles.label, { color: themeColors.label }]}>{t('unitGoals.responsibleUnit')}</Text>
                </View>
                {unitsLoading ? (
                  <View style={[styles.loadingContainer, { backgroundColor: isDark ? themeColors.section : tokens.colors.raw.gray["100"] }]}>
                    <ActivityIndicator size="small" color={tokens.colors.semantic.general["Main Color"]} />
                  </View>
                ) : (
                  <Dropdown
                    value={formData.unit}
                    options={unitOptionsDropdown}
                    placeholder={t('unitGoals.selectResponsible')}
                    onChange={(v) => updateField('unit', v)}
                  />
                )}
                {errors.unit && <Text style={styles.errorText}>{errors.unit}</Text>}
              </View>

              <View style={[styles.fieldHalf, { zIndex: 20, overflow: 'visible' }]}>
                <View style={styles.labelRow}>
                  <Text style={styles.requiredStar}>*</Text>
                  <Text style={[styles.label, { color: themeColors.label }]}>{t('goals.strategicGoal')}</Text>
                </View>
                {goalsLoading ? (
                  <View style={[styles.loadingContainer, { backgroundColor: isDark ? themeColors.section : tokens.colors.raw.gray["100"] }]}>
                    <ActivityIndicator size="small" color={tokens.colors.semantic.general["Main Color"]} />
                  </View>
                ) : (
                  <Dropdown
                    value={formData.strategicGoal}
                    options={goalOptions}
                    placeholder={t('goals.selectStrategicGoal')}
                    onChange={(v) => updateField('strategicGoal', v)}
                  />
                )}
              </View>
            </View>
          </View>
        </View>

        {/* KPIs Section */}
        <View style={[styles.mainCard, { backgroundColor: themeColors.mainCard, borderColor: themeColors.mainCardBorder, overflow: 'visible', zIndex: 1 }]}>
          <View
            style={[styles.kpiSectionWrapper, { backgroundColor: themeColors.section }]}
            onLayout={(event) => {
              kpiSectionY.current = event.nativeEvent.layout.y;
            }}
          >
            {/* Section Header */}
            <TouchableOpacity
              style={styles.kpiSectionHeader}
              onPress={() => setKpiSectionExpanded(!kpiSectionExpanded)}
            >
              {kpiSectionExpanded ? (
                <ChevronUpIcon size={20} color={themeColors.iconColor} />
              ) : (
                <ChevronDownIcon size={20} color={themeColors.iconColor} />
              )}
              <Text style={[styles.kpiSectionTitle, { color: themeColors.sectionTitle }]}>{t('kpis.performanceIndicators')}</Text>
            </TouchableOpacity>

            {kpiSectionExpanded && (
              <View style={styles.kpiSectionContent}>
                {/* Loading indicator for existing KPIs */}
                {existingKpisLoading ? (
                  <View style={styles.existingKpisLoading}>
                    <ActivityIndicator size="small" color={COLORS.primary} />
                    <Text style={[styles.existingKpisLoadingText, { color: themeColors.iconColor }]}>{t('kpis.loadingKPIs')}</Text>
                  </View>
                ) : (
                  <>
                    {/* Editable KPI Cards */}
                    {kpis.map((kpi, index) => renderKPICard(kpi, index))}

                    {/* Add KPI Button */}
                    <TouchableOpacity style={[styles.addKpiButton, { borderColor: themeColors.inputBorder }]} onPress={handleAddKPI}>
                      <Text style={[styles.addKpiButtonText, { color: themeColors.inputText }]}>{t('kpis.addPerformanceIndicator')}</Text>
                      <PlusIcon size={16} color={themeColors.inputText} />
                    </TouchableOpacity>
                  </>
                )}
              </View>
            )}
          </View>
        </View>

        {/* Footer Buttons */}
        <View style={styles.footer}>
          <Button
            variant="primary"
            onPress={handleSave}
            loading={loading}
          >
            {t('common.save')}
          </Button>
          <Button
            variant="secondary"
            onPress={requestCancel}
          >
            {t('common.cancel')}
          </Button>
        </View>
      </ScrollView>
      <KPIDeleteConfirmModal
        visible={kpiPendingDelete !== null}
        kpiId={kpiPendingDelete?.backendId ?? null}
        kpiName={kpiPendingDelete?.name}
        onConfirm={confirmDeleteKPI}
        onCancel={() => setKpiPendingDelete(null)}
      />
      <CancelConfirmModal
        visible={confirmVisible}
        onConfirm={confirmDiscard}
        onClose={dismissConfirm}
      />
    </PageContainer>
  );
}

const getStyles = (isRTL: boolean) => StyleSheet.create({
  scrollView: {
    flex: 1,
  },
  content: {
    padding: tokens.spacing.absolute["6"],
    paddingBottom: tokens.spacing.absolute["10"],
  },
  breadcrumb: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: tokens.spacing.absolute["2"],
    marginBottom: tokens.spacing.absolute["4"],
  },
  breadcrumbLink: {
    fontFamily: FONTS.regular,
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    color: tokens.colors.raw.gray["400"],
  },
  breadcrumbActive: {
    fontFamily: FONTS.medium,
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    color: COLORS.primary,
  },
  pageTitle: {
    fontFamily: FONTS.medium,
    fontSize: tokens.typography["heading 4"]["font size"],
    color: tokens.colors.semantic.general.foreground,
    textAlign: getTextAlign(isRTL),
    marginBottom: tokens.spacing.absolute["6"],
    fontWeight: '700',
  },
  mainCard: {
    backgroundColor: tokens.colors.raw.white,
    borderRadius: tokens.radii.absolute["radius-8"],
    borderWidth: tokens.spacing.absolute["px"],
    borderColor: tokens.colors.raw.slate["200"],
    padding: tokens.spacing.absolute["6"],
    marginBottom: tokens.spacing.absolute["6"],
  },
  section: {
    marginBottom: tokens.spacing.absolute["6"],
  },
  sectionTitle: {
    fontFamily: FONTS.medium,
    fontSize: tokens.typography.paragraph["paragraph font size"],
    color: tokens.colors.raw.gray["800"],
    textAlign: getTextAlign(isRTL),
    marginBottom: tokens.spacing.absolute["4"],
    fontWeight: '600',
  },
  row: {
    flexDirection: 'row',
    gap: tokens.spacing.absolute["4"],
    marginBottom: tokens.spacing.absolute["4"],
  },
  field: {
    marginBottom: tokens.spacing.absolute["4"],
  },
  fieldHalf: {
    flex: 1,
  },
  labelRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: tokens.spacing.absolute["1"],
    marginBottom: tokens.spacing.absolute["2"],
  },
  label: {
    fontFamily: FONTS.regular,
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    color: tokens.colors.raw.gray["700"],
  },
  requiredStar: {
    fontFamily: FONTS.regular,
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    color: tokens.colors.raw.red["500"],
  },
  input: {
    backgroundColor: tokens.colors.raw.white,
    borderWidth: tokens.spacing.absolute["px"],
    borderColor: tokens.colors.raw.slate["200"],
    borderRadius: tokens.radii.absolute["radius-8"],
    paddingHorizontal: tokens.spacing.absolute["3"],
    paddingVertical: tokens.spacing.absolute["2_5"],
    fontFamily: FONTS.regular,
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    color: tokens.colors.semantic.general.foreground,
    textAlign: getTextAlign(isRTL),
  },
  inputError: {
    borderColor: tokens.colors.raw.red["500"],
  },
  textArea: {
    minHeight: 80,
    textAlignVertical: 'top',
  },
  errorText: {
    fontFamily: FONTS.regular,
    fontSize: tokens.typography.paragraph["paragraph mini font size"],
    color: tokens.colors.raw.red["500"],
    textAlign: getTextAlign(isRTL),
    marginTop: tokens.spacing.absolute["1"],
  },
  weightInputRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: tokens.spacing.absolute["2"],
  },
  weightInput: {
    flex: 1,
    backgroundColor: tokens.colors.raw.white,
    borderWidth: tokens.spacing.absolute["px"],
    borderColor: tokens.colors.raw.slate["200"],
    borderRadius: tokens.radii.absolute["radius-8"],
    paddingHorizontal: tokens.spacing.absolute["3"],
    paddingVertical: tokens.spacing.absolute["2_5"],
    fontFamily: FONTS.regular,
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    color: tokens.colors.semantic.general.foreground,
    textAlign: getTextAlign(isRTL),
  },
  percentSymbol: {
    fontFamily: FONTS.regular,
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    color: tokens.colors.raw.gray["500"],
  },
  setWeightButton: {
    backgroundColor: tokens.colors.raw.slate["100"],
    borderRadius: tokens.radii.absolute["radius-8"],
    paddingHorizontal: tokens.spacing.absolute["3"],
    paddingVertical: tokens.spacing.absolute["2"],
  },
  setWeightButtonText: {
    fontFamily: FONTS.regular,
    fontSize: tokens.typography.paragraph["paragraph mini font size"],
    color: tokens.colors.raw.slate["500"],
  },
  dropdownContainer: {
    position: 'relative',
    zIndex: 1,
  },
  dropdownContainerOpen: {
    zIndex: 9999,
  },
  dropdownButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: tokens.spacing.absolute["2"],
    backgroundColor: tokens.colors.raw.white,
    borderWidth: tokens.spacing.absolute["px"],
    borderColor: tokens.colors.raw.slate["200"],
    borderRadius: tokens.radii.absolute["radius-8"],
    paddingHorizontal: tokens.spacing.absolute["3"],
    paddingVertical: tokens.spacing.absolute["2_5"],
    minHeight: 44,
  },
  dropdownButtonOpen: {
    borderColor: tokens.colors.semantic.general["Main Color"],
  },
  dropdownDisabled: {
    backgroundColor: tokens.colors.raw.gray["100"],
  },
  dropdownButtonText: {
    flex: 1,
    fontFamily: FONTS.regular,
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    color: tokens.colors.semantic.general.foreground,
    textAlign: getTextAlign(isRTL),
  },
  dropdownPlaceholder: {
    color: tokens.colors.raw.gray["400"],
  },
  dropdownMenu: {
    position: 'absolute',
    top: '100%',
    start: 0,
    end: 0,
    backgroundColor: tokens.colors.raw.white,
    borderWidth: tokens.spacing.absolute["px"],
    borderColor: tokens.colors.raw.slate["200"],
    borderRadius: tokens.radii.absolute["radius-8"],
    marginTop: tokens.spacing.absolute["1"],
    maxHeight: 200,
    boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.15)',
    elevation: 20,
    zIndex: 99999,
    overflow: 'hidden',
  },
  dropdownScroll: {
    maxHeight: 200,
  },
  dropdownItem: {
    paddingHorizontal: tokens.spacing.absolute["4"],
    paddingVertical: tokens.spacing.absolute["3"],
    backgroundColor: tokens.colors.raw.white,
  },
  dropdownItemActive: {
    backgroundColor: tokens.colors.raw.red["50"],
  },
  dropdownItemText: {
    fontFamily: FONTS.regular,
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    color: tokens.colors.semantic.general.foreground,
    textAlign: getTextAlign(isRTL),
  },
  dropdownItemTextActive: {
    color: tokens.colors.semantic.general["Main Color"],
    fontWeight: '600',
  },
  loadingContainer: {
    height: 44,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: tokens.colors.raw.gray["100"],
    borderRadius: tokens.radii.absolute["radius-8"],
  },
  loadingFullContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    gap: tokens.spacing.absolute["4"],
  },
  loadingText: {
    fontFamily: FONTS.regular,
    fontSize: tokens.typography.paragraph["paragraph font size"],
    color: tokens.colors.raw.gray["500"],
  },
  // KPI Section (inside mainCard)
  kpiSectionWrapper: {
    backgroundColor: tokens.colors.raw.slate["50"],
    borderRadius: tokens.radii.absolute["radius-8"],
  },
  kpiSectionHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: tokens.spacing.absolute["4"],
    paddingHorizontal: tokens.spacing.absolute["4"],
    gap: tokens.spacing.absolute["2"],
    minHeight: 44,
  },
  kpiSectionTitle: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontFamily: FONTS.medium,
    color: tokens.colors.semantic.general.foreground,
    textAlign: 'center',
  },
  kpiSectionContent: {
    padding: tokens.spacing.absolute["4"],
    paddingTop: tokens.spacing.absolute["0"],
  },
  // KPI Card
  kpiCard: {
    backgroundColor: tokens.colors.raw.white,
    borderRadius: tokens.radii.absolute["radius-8"],
    borderWidth: tokens.spacing.absolute["px"],
    borderColor: tokens.colors.raw.slate["200"],
    marginBottom: tokens.spacing.absolute["3"],
  },
  kpiCardHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: tokens.spacing.absolute["3"],
    paddingVertical: tokens.spacing.absolute["3"],
    backgroundColor: tokens.colors.raw.white,
    borderBottomWidth: tokens.spacing.absolute["px"],
    borderBottomColor: tokens.colors.raw.slate["200"],
  },
  kpiCardHeaderLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: tokens.spacing.absolute["1"],
  },
  kpiCardHeaderRight: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: tokens.spacing.absolute["3"],
  },
  kpiHeaderButton: {
    width: 44,
    height: 44,
    justifyContent: 'center',
    alignItems: 'center',
  },
  kpiCardNumber: {
    width: 28,
    height: 28,
    borderRadius: tokens.radii.absolute["radius-6"],
    backgroundColor: tokens.colors.raw.slate["100"],
    alignItems: 'center',
    justifyContent: 'center',
  },
  kpiCardNumberText: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontFamily: FONTS.medium,
    color: tokens.colors.semantic.general.foreground,
  },
  kpiCardName: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontFamily: FONTS.regular,
    color: tokens.colors.semantic.general.foreground,
    textAlign: getTextAlign(isRTL),
  },
  kpiCardContent: {
    padding: tokens.spacing.absolute["4"],
  },
  // Subsection (inside KPI card content)
  subsection: {
    backgroundColor: tokens.colors.raw.slate["50"],
    borderRadius: tokens.radii.absolute["radius-8"],
    padding: tokens.spacing.absolute["4"],
    marginBottom: tokens.spacing.absolute["4"],
  },
  subsectionTitle: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontFamily: FONTS.medium,
    color: tokens.colors.semantic.general.foreground,
    textAlign: 'center',
    marginBottom: tokens.spacing.absolute["4"],
  },
  fieldSmall: {
    flex: 1,
  },
  fieldLarge: {
    flex: 2,
  },
  dateInputContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: tokens.spacing.absolute["2"],
    backgroundColor: tokens.colors.raw.white,
    borderWidth: tokens.spacing.absolute["px"],
    borderColor: tokens.colors.raw.slate["200"],
    borderRadius: tokens.radii.absolute["radius-8"],
    paddingHorizontal: tokens.spacing.absolute["3"],
  },
  dateInput: {
    flex: 1,
    fontFamily: FONTS.regular,
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    color: tokens.colors.semantic.general.foreground,
    paddingVertical: tokens.spacing.absolute["2_5"],
  },
  addKpiButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: tokens.spacing.absolute["2"],
    paddingVertical: tokens.spacing.absolute["3"],
    paddingHorizontal: tokens.spacing.absolute["4"],
    borderWidth: tokens.spacing.absolute["px"],
    borderColor: tokens.colors.raw.slate["200"],
    borderRadius: tokens.radii.absolute["radius-8"],
    borderStyle: 'dashed',
    backgroundColor: tokens.colors.raw.white,
    minHeight: 44,
  },
  addKpiButtonText: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontFamily: FONTS.regular,
    color: tokens.colors.semantic.general.foreground,
  },
  footer: {
    flexDirection: 'row',
    direction: 'ltr' as any, // pin physical order: red Save always left, Cancel right (no RTL flip)
    justifyContent: 'flex-start',
    gap: tokens.spacing.absolute["3"],
  },
  // Existing KPIs styles - Matching Figma design
  existingKpisSection: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: tokens.spacing.absolute["5"],
    padding: tokens.spacing.absolute["3"],
  },
  existingKpisLoading: {
    flex: 1,
    padding: tokens.spacing.absolute["6"],
    alignItems: 'center',
    flexDirection: 'row',
    justifyContent: 'center',
    gap: tokens.spacing.absolute["2"],
  },
  existingKpisLoadingText: {
    fontFamily: FONTS.regular,
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    color: tokens.colors.raw.slate["500"],
  },
  kpiCardsContainer: {
    padding: tokens.spacing.absolute["4"],
    paddingTop: tokens.spacing.absolute["0"],
  },
  existingKpisEmpty: {
    flex: 1,
    padding: tokens.spacing.absolute["6"],
    alignItems: 'center',
  },
  existingKpisEmptyText: {
    fontFamily: FONTS.regular,
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    color: tokens.colors.raw.gray["400"],
  },
  existingKpisList: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: tokens.spacing.absolute["5"],
    width: '100%',
  },
  existingKpiCard: {
    flex: 1,
    minWidth: 250,
    backgroundColor: tokens.colors.raw.white,
    borderRadius: tokens.radii.absolute["radius-8"],
    borderWidth: tokens.spacing.absolute["px"],
    borderColor: tokens.colors.raw.slate["200"],
    padding: tokens.spacing.absolute["3"],
    gap: tokens.spacing.absolute["4"],
  },
  existingKpiName: {
    fontFamily: FONTS.regular,
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    color: tokens.colors.semantic.general.foreground,
    textAlign: getTextAlign(isRTL),
    lineHeight: 20,
  },
  existingKpiStats: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: tokens.spacing.absolute["2"],
  },
  existingKpiValueBadge: {
    backgroundColor: tokens.colors.raw.slate["100"],
    borderRadius: 100,
    paddingHorizontal: tokens.spacing.absolute["2"],
    paddingVertical: tokens.spacing.absolute["0_5"],
  },
  existingKpiValueText: {
    fontFamily: FONTS.numbers,
    fontWeight: '500',
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    color: tokens.colors.semantic.general.foreground,
    textAlign: 'center',
  },
  existingKpiProgressContainer: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: tokens.spacing.absolute["0"],
  },
  existingKpiProgressBar: {
    flex: 1,
    height: 8,
    backgroundColor: tokens.colors.raw.slate["100"],
    borderRadius: tokens.radii.absolute["radius-8"],
    overflow: 'visible',
  },
  existingKpiProgressFill: {
    height: '100%',
    borderRadius: tokens.radii.absolute["radius-8"],
  },
  existingKpiDotOuter: {
    width: 14,
    height: 14,
    borderRadius: tokens.radii.absolute["radius-7"],
    borderWidth: 2,
    borderColor: '#2CA02C',
    backgroundColor: '#FCFCFC',
    justifyContent: 'center',
    alignItems: 'center',
    marginStart: -5,
    zIndex: 2,
  },
  existingKpiDotInner: {
    width: 8,
    height: 8,
    borderRadius: tokens.radii.absolute["radius-4"],
    backgroundColor: '#2CA02C',
  },
  existingKpiLink: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-start',
    alignSelf: 'flex-start',
    gap: tokens.spacing.absolute["2"],
    paddingVertical: 3,
    minHeight: 24,
  },
  existingKpiLinkText: {
    fontFamily: FONTS.regular,
    fontSize: tokens.typography.paragraph["paragraph mini font size"],
    color: tokens.colors.semantic.general.foreground,
    textAlign: getTextAlign(isRTL),
  },
});

const styles = getStyles(true);
